import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, CopyButton, EmptyState, Field, Icon, PageHeader, Skeleton, SkeletonText, TableSkeleton, Tabs, cx,
} from '../components/ui';

// Chat templates carry this `type`. It is not a channel: there is one row per reply and every
// channel renders it. `type` only separates chat copy from email copy. Must match
// CHANNEL_TEMPLATE_TYPE in backend/api/routes/templates.py and backend/services/templates.py.
const CHANNEL_TEMPLATE_TYPE = 'channel';

// `cmd_` is a PREFIX: a shared row name is the bare kind, no channel in front of it.
const isCommand = (template) => !!template.name && template.name.startsWith('cmd_');

// Dynamic per-user variables injected by the rendering code at runtime.
// Shared admin-managed variables (support_url, manual_url, register_url, …)
// are NOT listed here — they're already visible on the Shared variables tab,
// and render_template auto-merges them into every template.
const COMMON_VARIABLES = ['first_name', 'username'];

// The row name IS the kind now that no channel prefixes it, so the display-name and
// variable maps below are keyed directly on it. Kept as a named function because those maps
// are looked up in several places and it documents the relationship.
const kindOf = (name) => name || '';

const VARIABLES_BY_KIND = {
  'unregistered': ['external_id', 'telegram_id'],
  'wrong_bot': ['correct_bot_handle'],
  'welcome': [],
  'inactive': [],
  'quota_exceeded': [],
  'container_starting': [],
  'container_stopped': [],
  'cmd_aiuto': [],
  'cmd_riavvia': ['sessions_cleared'],
  'cmd_reset': ['sessions_cleared'],
  'cmd_reboot': ['rebooted'],
  'cmd_piano': ['tier_name', 'renewal_date'],
  'cmd_upgrade': [],
  'cmd_downgrade': [],
  'cmd_disdetta': [],
  'cmd_consumi': ['quotas_perc'],
  'cmd_supporto': [],
  'cmd_manuale': [],
  // The workspace storage warnings (backend/services/workspace_usage.py).
  'storage_warning': ['used', 'cap', 'percent'],
};

const NAMES_BY_KIND = {
  'unregistered': 'Unregistered user',
  'wrong_bot': 'Wrong bot redirect',
  'inactive': 'Inactive or unsubscribed user',
  'welcome': 'Welcome after pairing',
  'first_ready': 'Agent ready (first time, coupon signup)',
  // Sent once, when a user's first message starts building their workspace and agent.
  'first_boot': 'First message: preparing the workspace',
  'quota_exceeded': 'Quota exceeded',
  // Sent to a customer whose agent had never run. Since workspaces are created at the first
  // message, that message wakes them like a hibernated agent and nothing sends this any more.
  'container_starting': 'Agent starting (no longer sent)',
  'container_stopped': 'Agent stopped',
  'cmd_aiuto': 'Commands help',
  'cmd_riavvia': 'Conversation reset (legacy /riavvia)',
  'cmd_reset': 'Reset chat (/reset)',
  'cmd_reboot': 'Restart agent (/reboot)',
  'cmd_piano': 'Subscription details',
  'cmd_upgrade': 'Plan upgrade',
  'cmd_downgrade': 'Plan downgrade',
  'cmd_disdetta': 'Subscription cancellation',
  'cmd_consumi': 'Usage summary',
  'cmd_supporto': 'Support contact',
  'cmd_manuale': 'User manual',
  'storage_warning': 'Workspace almost full (75% of the cap)',
};

const getVariables = (name) => {
  const seen = new Set();
  return [...COMMON_VARIABLES, ...(VARIABLES_BY_KIND[kindOf(name)] || [])].filter(v => {
    if (seen.has(v)) return false;
    seen.add(v);
    return true;
  });
};

const getDisplayName = (name) => NAMES_BY_KIND[kindOf(name)] || name;

// `channelLabel`, the `inherited` list, `createOverride` and `removeOverride` used to live
// here. They implemented per-channel copies of a reply, which is exactly what is no longer
// allowed. There is one row per reply; editing it changes every channel.

// The list's one-line preview of a message.
const firstLine = (text) => (text || '').trim().split('\n')[0];

// The one shared set, shown as two tabs only because a command row carries two more fields:
// the word itself, and the description Telegram shows in its "/" menu.
//
// "shared by every channel" is asserted by tests/test_per_channel_console.py, and rightly:
// editing one row changes what every channel sends. It used to be in the InfoTip, which is
// the wrong place for it — a fact that makes an edit surprising has to be visible without
// hovering. It is the card's subtitle on both tabs.
const KINDS = {
  replies: {
    heading: 'Replies',
    lede: 'Sent by the platform itself, for example to someone without access or while their agent starts. One row per reply, shared by every channel.',
  },
  commands: {
    heading: 'Slash commands',
    lede: 'What the platform replies when a user types a slash command, and the word they type. One row per command, shared by every channel.',
  },
};

// A sidebar that wraps under the editor instead of squeezing it: beside the editor the list
// keeps about 260px and the editor over half the card; narrower than that, both go full width,
// stacked. Flex-wrap rather than a media query, so it follows the card, not the window.
const LIST_COL = { flex: '1 1 260px' };
const DETAIL_COL = { flex: '999 1 0%', minWidth: '55%' };

/* ── Shared variables ───────────────────────────────────────────────────── */

function VariablesSection({ onCount }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [draft, setDraft] = useState({ value: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [newVar, setNewVar] = useState({ name: '', value: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const fetch_ = async () => {
    try {
      const resp = await apiFetch('/api/template-variables');
      if (resp.ok) {
        setRows(await resp.json());
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } catch (e) {
      // No toast, as before. A failed load shows its own state in place of the table
      // instead of passing for an empty list.
      setLoadFailed(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch_(); }, []);

  // A failed REFRESH keeps the rows already on screen; only a first load that failed has
  // nothing to show.
  const unavailable = loadFailed && rows.length === 0;

  // The page shows this count on the section's tab. Unknown while loading or unavailable, so
  // the tab shows no number rather than a wrong one.
  useEffect(() => {
    onCount?.(loading || unavailable ? null : rows.length);
  }, [loading, unavailable, rows.length, onCount]);

  const retry = () => { setLoading(true); fetch_(); };

  const startEdit = (row) => {
    setCreating(false);
    setEditingName(row.name);
    setDraft({ value: row.value || '', description: row.description || '' });
  };

  const startCreate = () => {
    setEditingName(null);
    setCreating(true);
  };

  const closeModal = () => {
    if (saving) return;
    setEditingName(null);
    setCreating(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const resp = await apiFetch(`/api/template-variables/${editingName}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (resp.ok) {
        toast.success(`Updated {{${editingName}}}`);
        setEditingName(null);
        fetch_();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch (e) { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const add = async () => {
    if (!newVar.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const resp = await apiFetch('/api/template-variables', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newVar),
      });
      if (resp.ok) {
        toast.success(`Added {{${newVar.name.toLowerCase()}}}`);
        setCreating(false);
        setNewVar({ name: '', value: '', description: '' });
        fetch_();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create');
      }
    } catch (e) { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const remove = async (name) => {
    // The native version put a literal `\n` and a raw `{{name}}` in a plain-text
    // box. Both render properly now.
    if (!await confirm({
      title: `Delete variable ${name}?`,
      body: (
        <p>
          Templates referencing it will render the literal{' '}
          <code>{`{{${name}}}`}</code> instead of a value.
        </p>
      ),
      confirmLabel: 'Delete variable',
    })) return;
    try {
      const resp = await apiFetch(`/api/template-variables/${name}`, { method: 'DELETE' });
      if (resp.ok) { toast.success(`Deleted {{${name}}}`); fetch_(); }
      else { toast.error('Failed to delete'); }
    } catch (e) { toast.error('Failed to delete'); }
  };

  // One dialog for both jobs. Create owns `newVar`, edit owns `draft`: the two payloads keep
  // exactly the shapes they had, {name, value, description} and {value, description}.
  const values = creating ? newVar : draft;
  const setValues = creating ? setNewVar : setDraft;
  // What the backend will store: stripped, braces dropped, lower-cased.
  const typedName = newVar.name.trim().replace(/[{}]/g, '').toLowerCase();

  let body;
  if (loading) {
    body = <TableSkeleton rows={3} cols={4} label="Loading shared variables" />;
  } else if (unavailable) {
    body = (
      <EmptyState
        icon="alertCircle"
        title="Shared variables could not be loaded"
        action={(
          <button type="button" className="btn btn-secondary" onClick={retry}>
            <Icon name="refresh" /> Try again
          </button>
        )}
      >
        Nothing was changed. The list is just unavailable right now.
      </EmptyState>
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon="code"
        title="No shared variables yet"
        action={<button type="button" className="btn btn-primary" onClick={startCreate}>Add variable</button>}
      >
        Define a value once, such as a support address or a billing link, and use it in any
        message as <code>{'{{name}}'}</code>.
      </EmptyState>
    );
  } else {
    body = (
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              <th>Variable</th>
              <th>Value</th>
              <th>Description</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const placeholder = `{{${row.name}}}`;
              return (
                <tr key={row.name}>
                  <td data-label="Variable">
                    <span className="inline-row">
                      <span className="cell-primary mono">{placeholder}</span>
                      <CopyButton value={placeholder} label={`Copy ${placeholder}`} />
                    </span>
                  </td>
                  <td data-label="Value" className="wrap">
                    {row.value
                      ? <code>{row.value}</code>
                      : <span className="text-faint">Not set</span>}
                  </td>
                  <td data-label="Description" className="wrap">
                    <span className="text-sm text-muted">{row.description || '—'}</span>
                  </td>
                  <td data-label="Actions">
                    <div className="row gap-1 row-end">
                      <button type="button" className="btn btn-ghost btn-sm btn-icon"
                              aria-label={`Edit variable ${row.name}`} title="Edit variable"
                              onClick={() => startEdit(row)}>
                        <Icon name="edit" />
                      </button>
                      <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                              aria-label={`Delete variable ${row.name}`} title="Delete variable"
                              onClick={() => remove(row.name)}>
                        <Icon name="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="card card-flush">
      <div className="card-header">
        <div>
          <h2 className="card-title">
            Shared variables
            <InfoTip label="About shared variables" width={340}>
              <p>
                Define once, use as <code>{'{{name}}'}</code> in any message. Same value for
                every user.
              </p>
              <p>
                Per-user placeholders like <code>{'{{first_name}}'}</code> win on a name clash.
              </p>
              <p>
                An unknown <code>{'{{name}}'}</code> is left as written, so a typo shows up in
                the user&apos;s chat.
              </p>
            </InfoTip>
          </h2>
          <p className="card-subtitle">
            Values you can drop into any message as <code>{'{{name}}'}</code>. The same for
            every user: a billing link, a support address.
          </p>
        </div>
        <div className="card-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={startCreate}>
            <Icon name="plus" /> Add variable
          </button>
        </div>
      </div>

      {body}

      <Modal
        open={creating || editingName !== null}
        onClose={closeModal}
        title={creating ? 'New shared variable' : `Edit {{${editingName}}}`}
        description={creating ? null : 'The new value applies to every message that uses it.'}
        maxWidth={520}
      >
        <form onSubmit={(e) => { e.preventDefault(); if (creating) add(); else save(); }}>
          {creating && (
            <Field
              label="Name"
              htmlFor="var-name"
              required
              hint={(
                <>
                  Letters, digits and underscores, saved in lower case. Messages use it
                  as <code>{`{{${typedName || 'name'}}}`}</code>.
                </>
              )}
            >
              <input id="var-name" className="form-input mono" value={newVar.name}
                     onChange={e => setNewVar(v => ({ ...v, name: e.target.value }))}
                     placeholder="e.g. website_url" autoFocus autoComplete="off" spellCheck={false} />
            </Field>
          )}
          <Field label="Value" htmlFor="var-value" hint="The same for every user.">
            <input id="var-value" className="form-input" value={values.value}
                   onChange={e => setValues(v => ({ ...v, value: e.target.value }))}
                   autoFocus={!creating} autoComplete="off" />
          </Field>
          <Field label="Description" htmlFor="var-description" className="mb-0"
                 hint="Optional. What it is for, so the next admin knows.">
            <input id="var-description" className="form-input" value={values.description}
                   onChange={e => setValues(v => ({ ...v, description: e.target.value }))} />
          </Field>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? <><span className="spinner-sm" /> Saving…</>
                : (creating ? 'Add variable' : 'Save variable')}
            </button>
          </div>
        </form>
      </Modal>

      {confirmDialog}
    </div>
  );
}

/* ── Message templates ──────────────────────────────────────────────────── */

// Shaped like the list-and-editor it stands in for, so nothing jumps when the rows land.
function TemplatesSkeleton() {
  return (
    <div className="row-wrap items-start gap-5" role="status" aria-busy="true">
      <span className="sr-only">Loading templates</span>
      <div className="stack gap-2" style={LIST_COL} aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={44} />)}
      </div>
      <div className="stack gap-4" style={DETAIL_COL} aria-hidden="true">
        <Skeleton width="40%" height={16} />
        <SkeletonText lines={2} />
        <Skeleton height={180} />
      </div>
    </div>
  );
}

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [tab, setTab] = useState('replies');
  // The selected row per tab, so switching tabs and back returns to the same message.
  const [selectedIds, setSelectedIds] = useState({ replies: null, commands: null });
  const [variableCount, setVariableCount] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCommandName, setEditCommandName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const listRef = useRef(null);
  const detailRef = useRef(null);

  // One set. This page used to carry a channel selector, an "inherited from Telegram"
  // section and buttons to create and delete per-channel copies. All of that is gone with the
  // per-channel model: editing a reply here changes it on every channel at once.
  const fetchTemplates = async () => {
    try {
      const resp = await apiFetch(`/api/templates?type=${CHANNEL_TEMPLATE_TYPE}`);
      if (resp.ok) {
        setTemplates(await resp.json());
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } catch (e) {
      toast.error('Failed to load templates');
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); fetchTemplates(); }, []);

  const groups = {
    replies: templates.filter(t => !isCommand(t)),
    commands: templates.filter(t => isCommand(t)),
  };
  const kind = tab === 'commands' ? 'commands' : 'replies';
  const list = groups[kind];
  const selected = list.find(t => t.id === selectedIds[kind]) || list[0] || null;

  const editing = templates.find(t => t.id === editingId) || null;
  const dirty = !!editing && (
    editBody !== (editing.body_template || '')
    || editDescription !== (editing.description || '')
    || editCommandName !== (editing.command_name || '')
  );

  // True when nothing unsaved would be lost, or the admin agreed to lose it. The list sits
  // next to the editor, so one stray click on another row must not throw an edit away.
  const discardOk = async () => {
    if (!dirty) return true;
    return confirm({
      title: `Discard changes to ${getDisplayName(editing.name)}?`,
      body: <p>Your edits have not been saved. The message keeps its current text on every channel.</p>,
      confirmLabel: 'Discard changes',
    });
  };

  const startEdit = async (template) => {
    if (editingId !== null && editingId !== template.id && !await discardOk()) return;
    setEditingId(template.id);
    setEditBody(template.body_template || '');
    setEditDescription(template.description || '');
    setEditCommandName(template.command_name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
    setEditDescription('');
    setEditCommandName('');
  };

  const selectTemplate = async (template) => {
    if (selected && template.id === selected.id) return;
    // Moving off the message being edited ends the edit.
    if (selected && editingId === selected.id) {
      if (!await discardOk()) return;
      cancelEdit();
    }
    setSelectedIds(prev => ({ ...prev, [kind]: template.id }));
    // Stacked on a narrow card, the editor sits under the list, out of view: bring it up.
    const listEl = listRef.current;
    const detailEl = detailRef.current;
    if (listEl && detailEl && detailEl.offsetTop >= listEl.offsetTop + listEl.offsetHeight) {
      detailEl.scrollIntoView({ block: 'start' });
    }
  };

  const saveEdit = async (template) => {
    const id = template.id;
    const isCmd = isCommand(template);
    const payload = {
      body_template: editBody,
      description: editDescription,
    };
    if (isCmd) {
      // Client-side validation mirrors the backend regex so we fail fast.
      const cn = editCommandName.trim().toLowerCase().replace(/^\//, '');
      if (!cn) {
        toast.error('Command name is required (or use the Templates API to clear it)');
        return;
      }
      if (!/^[a-z0-9_]{1,32}$/.test(cn)) {
        toast.error('Command name must match [a-z0-9_]{1,32}');
        return;
      }
      if (editDescription.trim().length < 3 || editDescription.trim().length > 256) {
        toast.error('Description must be 3-256 characters (Telegram setMyCommands constraint)');
        return;
      }
      payload.command_name = cn;
    }
    setSaving(true);
    try {
      const resp = await apiFetch(`/api/templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        toast.success(isCmd ? 'Template updated, pushing to Telegram bots' : 'Template updated');
        setEditingId(null);
        fetchTemplates();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update template');
      }
    } catch (e) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const retry = () => { setLoading(true); fetchTemplates(); };

  // The link that sends here unmounts with the panel it sits in, so focus goes to the tab it
  // opened rather than being dropped on the page.
  const showVariables = () => {
    setTab('variables');
    document.getElementById('templates-tab-variables')?.focus();
  };

  // Reference only, with a copy button each: the editor has no insert-at-cursor.
  const renderVariables = (template) => {
    const vars = getVariables(template.name);
    if (vars.length === 0) return null;
    return (
      <div className="form-group mb-0">
        <div className="form-label" id="template-vars-label">Variables</div>
        <ul className="row-wrap gap-2" aria-labelledby="template-vars-label">
          {vars.map(v => (
            <li key={v} className="inline-row">
              <code>{`{{${v}}}`}</code>
              <CopyButton value={`{{${v}}}`} label={`Copy {{${v}}}`} />
            </li>
          ))}
        </ul>
        <p className="form-hint">
          Filled in for each user when the message is sent.{' '}
          <button type="button" className="btn-link" onClick={showVariables}>
            Shared variables
          </button>{' '}
          work in every message too.
        </p>
      </div>
    );
  };

  const renderRead = (template) => {
    const isCmd = isCommand(template);
    return (
      <>
        {isCmd && (
          <div className="form-group">
            <div className="form-label">Command</div>
            {template.command_name ? (
              <p>Reply for <code>/{template.command_name}</code></p>
            ) : (
              <p className="text-muted">
                Disabled. Without a command word users cannot trigger it, and it is not in the
                Telegram menu.
              </p>
            )}
          </div>
        )}
        <div className="form-group">
          <div className="form-label">Message</div>
          {template.body_template ? (
            <pre className="code-block pre-wrap break m-0" style={{ maxHeight: 420, overflowY: 'auto' }}>
              {template.body_template}
            </pre>
          ) : (
            <p className="empty-inline card-inset">Not configured</p>
          )}
        </div>
        {renderVariables(template)}
      </>
    );
  };

  const renderEdit = (template) => {
    const isCmd = isCommand(template);
    const descLength = editDescription.trim().length;
    return (
      <form onSubmit={(e) => { e.preventDefault(); saveEdit(template); }}
            aria-label={`Edit ${getDisplayName(template.name)}`}>
        <Field
          label="Description"
          htmlFor="tpl-description"
          hint={isCmd
            ? <>{'Also shown in the Telegram "/" menu, so 3–256 characters. '}<span className="num">{descLength} / 256</span></>
            : 'A note for admins on when this message is sent.'}
        >
          <textarea id="tpl-description" className="form-input" rows={2}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Functional description" />
        </Field>

        {isCmd && !template.command_name && (
          <div className="notice notice-warn">
            <Icon name="alert" />
            <div className="notice-body">
              This command is disabled. Saving needs a command word, which turns it back on.
            </div>
          </div>
        )}

        {isCmd && (
          <Field label="Command" htmlFor="tpl-command"
                 hint="Letters, digits, underscore. Pushed to all bots on save.">
            <div className="input-affix">
              <span className="affix-icon mono" aria-hidden="true">/</span>
              <input
                id="tpl-command"
                className="form-input mono"
                value={editCommandName}
                onChange={(e) => setEditCommandName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="command_name"
                maxLength={32}
                autoComplete="off"
                spellCheck={false}
                aria-describedby="tpl-command-hint"
              />
            </div>
          </Field>
        )}

        <Field label="Message" htmlFor="tpl-body"
               hint={`${editBody.length.toLocaleString()} character${editBody.length === 1 ? '' : 's'}`}>
          <textarea id="tpl-body" className="form-input mono" rows={10}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)} />
        </Field>

        {renderVariables(template)}

        <div className="form-actions between">
          <p className="text-sm text-muted">
            Saving changes this {isCmd ? 'command' : 'reply'} on every channel.
          </p>
          <div className="row gap-2">
            <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save template'}
            </button>
          </div>
        </div>
      </form>
    );
  };

  const renderDetail = (template) => {
    const isEditing = editingId === template.id;
    return (
      <>
        <div className="subhead">
          <div className="min-w-0">
            {/* h3, not h4: this sits directly under the card's h2, so an
                h4 skipped a level in the document outline. */}
            <h3 id="template-detail-title" className="subhead-title text-md">
              {getDisplayName(template.name)}
            </h3>
            {!isEditing && (
              <p className="subhead-desc">
                {template.description || <span className="text-faint">No description</span>}
              </p>
            )}
          </div>
          {!isEditing && (
            <button type="button" className="btn btn-secondary btn-sm shrink-0"
                    onClick={() => startEdit(template)}>
              <Icon name="edit" /> Edit template
            </button>
          )}
        </div>
        {isEditing ? renderEdit(template) : renderRead(template)}
      </>
    );
  };

  const listSub = (template) => {
    if (isCommand(template)) {
      return template.command_name
        ? <span className="cell-sub mono truncate">/{template.command_name}</span>
        : <span className="cell-sub text-faint">Disabled</span>;
    }
    const preview = firstLine(template.body_template);
    return preview
      ? <span className="cell-sub truncate">{preview}</span>
      : <span className="cell-sub text-faint">Not configured</span>;
  };

  const editBadge = (template) => {
    if (template.id !== editingId) return null;
    return dirty
      ? <Badge tone="warn" dot>Unsaved</Badge>
      : <Badge tone="accent">Editing</Badge>;
  };

  const counted = !loading && !(loadFailed && templates.length === 0);
  const tabs = [
    { value: 'replies', label: 'Replies', icon: 'message', count: counted ? groups.replies.length : null },
    { value: 'commands', label: 'Slash commands', icon: 'terminal', count: counted ? groups.commands.length : null },
    { value: 'variables', label: 'Shared variables', icon: 'code', count: variableCount },
  ];
  const kindInfo = KINDS[kind];

  let content;
  if (loading) {
    content = <TemplatesSkeleton />;
  } else if (loadFailed && templates.length === 0) {
    content = (
      <EmptyState
        icon="alertCircle"
        title="Templates could not be loaded"
        action={(
          <button type="button" className="btn btn-secondary" onClick={retry}>
            <Icon name="refresh" /> Try again
          </button>
        )}
      >
        Nothing was changed. The list is just unavailable right now.
      </EmptyState>
    );
  } else if (list.length === 0) {
    content = (
      <EmptyState icon="templates" title="No templates found">
        Run the migration to seed defaults.
      </EmptyState>
    );
  } else {
    content = (
      <div className="row-wrap items-start gap-5">
        <div ref={listRef} className="min-w-0" style={LIST_COL}>
          <div className="table-wrap bordered">
            {/* Fixed layout, so the column is the wrapper's width and the first-line
                preview truncates instead of stretching the table past the card. */}
            <table className="table table-compact table-fixed">
              <caption className="sr-only">{kindInfo.heading}</caption>
              <tbody>
                {list.map(t => {
                  const isSelected = !!selected && t.id === selected.id;
                  return (
                    <tr key={t.id} className={isSelected ? 'row-selected' : undefined}>
                      <td className="wrap">
                        <button
                          type="button"
                          className="row between gap-2 w-full text-left"
                          aria-current={isSelected ? 'true' : undefined}
                          aria-controls="template-detail"
                          onClick={() => selectTemplate(t)}
                        >
                          <span className="grow min-w-0">
                            <span className={cx('cell-primary', isSelected && 'text-accent')}>
                              {getDisplayName(t.name)}
                            </span>
                            {listSub(t)}
                          </span>
                          {editBadge(t)}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <section ref={detailRef} id="template-detail" style={DETAIL_COL}
                 aria-labelledby="template-detail-title">
          {selected && renderDetail(selected)}
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader description="What the platform itself says, and the variables those messages share.">
        <h1 className="page-title">Templates</h1>
      </PageHeader>

      <div className="page-tabs">
        <Tabs tabs={tabs} value={tab} onChange={setTab} variant="line"
              label="Template sections" idPrefix="templates" />
      </div>

      {tab !== 'variables' && (
        <div role="tabpanel" id={`templates-panel-${kind}`} aria-labelledby={`templates-tab-${kind}`}>
          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">
                  {kindInfo.heading}
                  {/* The lede below already says what these are and that one row serves every
                      channel. Dropped as recoverable from the fields themselves: a command's
                      description is what Telegram shows in its menu, which is why that field is
                      required and length-limited. */}
                  <InfoTip label="About message templates" width={360}>
                    <p>
                      No per-channel variant, by design. If a reply must differ by channel, put a{' '}
                      <code>{'{{variable}}'}</code> in the shared copy.
                    </p>
                    <p>
                      Renaming a slash command needs no deploy: dispatch matches the row, not the
                      word. Switching one off entirely is API-only.
                    </p>
                    <p>
                      Changes reach the channels in about 15 seconds.
                    </p>
                  </InfoTip>
                </h2>
                <p className="card-subtitle">{kindInfo.lede}</p>
              </div>
            </div>

            {content}

            {/* An "Inherited from Telegram" section listed the rows a channel served but did not
                own, each with a button to fork its own copy. Both are gone: nothing inherits,
                because nothing has a per-channel copy to inherit from. */}
          </div>
        </div>
      )}

      {/* Mounted even while hidden, so the tab shows its count from the start. */}
      <div role="tabpanel" id="templates-panel-variables" aria-labelledby="templates-tab-variables"
           hidden={tab !== 'variables'}>
        <VariablesSection onCount={setVariableCount} />
      </div>

      {confirmDialog}
    </div>
  );
}

export default Templates;
